import React, { useImperativeHandle, useMemo } from 'react';
import { useDropzone, } from 'react-dropzone';
import mime from 'mime-types';
import classnames from 'classnames';
import UploadIcon from 'components/v2/icons/UploadIcon';
import CircleButton from 'components/v2/buttons/CircleButton';
import { getFilesWithContentType } from 'utils/files/files';
export const DROPZONE_VARIANTS = Object.freeze({
    BORDERED: 'bordered',
    BUTTON: 'button',
});
const VARIANT_STYLES = {
    [DROPZONE_VARIANTS.BORDERED]: {
        style: null,
        className: 'flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:border-gray-400 transition-colors',
    },
    [DROPZONE_VARIANTS.BUTTON]: {
        style: null,
        className: null,
    },
};
export function acceptToObject(accept) {
    if (!accept)
        return undefined;
    if (typeof accept === 'object' && !Array.isArray(accept))
        return accept;
    const tokens = (Array.isArray(accept) ? accept : accept.split(','))
        .map((s) => s.trim())
        .filter(Boolean);
    const result = {};
    for (const token of tokens) {
        if (token.startsWith('.')) {
            const ext = token.toLowerCase();
            const mimeType = mime.lookup(ext.slice(1)) || 'application/octet-stream';
            if (!result[mimeType])
                result[mimeType] = [];
            if (!result[mimeType].includes(ext))
                result[mimeType].push(ext);
        }
        else if (token.includes('/')) {
            if (!result[token])
                result[token] = [];
        }
    }
    return Object.keys(result).length > 0 ? result : undefined;
}
export const CargomaticDropzone = React.forwardRef(function CargomaticDropzone({ disabled = false, style, className, onDrop, onDropAccepted, onDropRejected, multiple = true, minSize, maxSize, accept, acceptedFiles, dropzoneMessage = 'Drop a file to upload', button, showIcon = true, dataTestId, variant = DROPZONE_VARIANTS.BUTTON, children, }, ref) {
    const acceptObject = useMemo(() => acceptToObject(accept !== null && accept !== void 0 ? accept : acceptedFiles), [accept, acceptedFiles]);
    const wrapWithContentType = (handler) => (files) => {
        if (!handler)
            return;
        if (!files || !files.length) {
            handler(files);
            return;
        }
        handler(getFilesWithContentType(files));
    };
    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: onDrop ? wrapWithContentType(onDrop) : undefined,
        onDropAccepted: onDropAccepted
            ? wrapWithContentType(onDropAccepted)
            : undefined,
        onDropRejected,
        accept: acceptObject,
        multiple,
        disabled,
        minSize,
        maxSize,
    });
    useImperativeHandle(ref, () => ({ open }), [open]);
    const variantConfig = VARIANT_STYLES[variant] || VARIANT_STYLES[DROPZONE_VARIANTS.BUTTON];
    const mergedStyle = variantConfig.style || style
        ? Object.assign(Object.assign({}, (variantConfig.style || {})), (style || {})) : undefined;
    const mergedClassName = classnames(variantConfig.className, className) || undefined;
    const defaultContent = (React.createElement("div", { "data-testid": "div-18236de0", className: classnames({
            flex: true,
            'm-auto': showIcon,
            'space-x-2': showIcon,
            'items-center': showIcon,
            'justify-end': showIcon,
        }) }, button ? (React.createElement(CircleButton, { dataTestId: "CircleButton-bf5a7c6e", onClick: () => { }, size: "small", color: "blue" },
        showIcon && React.createElement(UploadIcon, { className: "w-5 h-5" }),
        React.createElement("div", { "data-testid": "div-9135b438", className: "text-sm" }, dropzoneMessage))) : (React.createElement(React.Fragment, null,
        showIcon && React.createElement(UploadIcon, { className: "w-5 h-5" }),
        React.createElement("div", { "data-testid": "div-260ed7bf", className: "text-sm" }, dropzoneMessage)))));
    const rootProps = getRootProps({
        style: mergedStyle,
        className: mergedClassName,
    });
    return (React.createElement("div", Object.assign({}, rootProps, (dataTestId ? { 'data-testid': dataTestId } : {})),
        React.createElement("input", Object.assign({}, getInputProps())), children !== null && children !== void 0 ? children : defaultContent));
});
CargomaticDropzone.displayName = 'CargomaticDropzone';
